import { useState, useEffect, useMemo, useCallback } from 'react';
import { PageHeader, Skeleton } from '@solid/ui';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { announcementsApi, projectsApi, Announcement } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { useDraft } from '@/hooks/useDraft';
import { cn } from '@/lib/utils';

function toLocalDatetimeInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const statusConfig = {
  active: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10', label: 'Active' },
  scheduled: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Scheduled' },
  expired: { icon: XCircle, color: 'text-grey-400', bg: 'bg-grey-100', label: 'Expired' },
  draft: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-100', label: 'Draft' },
};

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcement?: Announcement | null;
}

function AnnouncementModal({ isOpen, onClose, announcement }: AnnouncementModalProps) {
  const queryClient = useQueryClient();
  const titleDraft = useDraft('draft:announcement:title');
  const contentDraft = useDraft('draft:announcement:content');
  const [title, setTitle] = useState(() => titleDraft.read());
  const [content, setContent] = useState(() => contentDraft.read());
  const [allProjects, setAllProjects] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [listExpanded, setListExpanded] = useState(false);
  
  // Sync form state when announcement changes (for edit mode)
  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title || '');
      setContent(announcement.content || '');
      setSelectedProjectIds(announcement.projects?.map(p => p.id) || []);
      setStartsAt(
        announcement.startsAt ? toLocalDatetimeInput(new Date(announcement.startsAt)) : ''
      );
      setEndsAt(
        announcement.endsAt ? toLocalDatetimeInput(new Date(announcement.endsAt)) : ''
      );
      setAllProjects(false);
      setNotifyUsers(false);
      setError(null);
      setOrgSearch('');
    } else {
      // Reset form for new announcement
      setTitle('');
      setContent('');
      setSelectedProjectIds([]);
      setStartsAt('');
      setEndsAt('');
      setAllProjects(false);
      setNotifyUsers(false);
      setError(null);
      setOrgSearch('');
      setExpandedOrgs(new Set());
      setListExpanded(false);
    }
  }, [announcement, isOpen]);
  
  const user = useAuthStore((s) => s.user);

  // Fetch available projects
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    enabled: isOpen,
  });

  // Group projects by organization
  const projectsByOrg = useMemo(() => {
    if (!projects) return [];

    // Build org name map from API-provided data. The API resolves BOTH the owning
    // org (project.organization) and the client org a project is assigned to
    // (project.clientOrganization) — client orgs must be included here or every
    // client project the current user isn't a member of falls through to 'Unknown'.
    const orgInfoFromApi = new Map<string, string>();
    for (const project of projects) {
      if (project.organization?.name && !orgInfoFromApi.has(project.organizationId)) {
        orgInfoFromApi.set(project.organizationId, project.organization.name);
      }
      const clientOrg = project.clientOrganization;
      if (clientOrg?.id && clientOrg.name && !orgInfoFromApi.has(clientOrg.id)) {
        orgInfoFromApi.set(clientOrg.id, clientOrg.name);
      }
    }

    // Fallback: JWT org names (for clientOrganizationIds not found above)
    const jwtOrgNames = new Map<string, { name: string; type: string }>();
    if (user?.organizations) {
      for (const org of user.organizations) {
        jwtOrgNames.set(org.id, { name: org.name, type: org.type });
      }
    }

    // Build groups: use the client org if the project is assigned to one, else the owner org
    const groups = new Map<string, { orgName: string; orgType: string; unresolved: boolean; projects: Array<{ id: string; name: string }> }>();

    for (const project of projects) {
      const clientOrgId = project.clientOrganization?.id
        || (project.settings as any)?.clientOrganizationId;
      const effectiveOrgId = clientOrgId || project.organizationId;

      // Resolve name: API data first, then JWT. Anything left is an org the
      // Accounts service no longer knows about (deleted, or Hub access revoked)
      // — surface the ID so it can be tracked down and reassigned.
      const resolvedName = orgInfoFromApi.get(effectiveOrgId)
        || jwtOrgNames.get(effectiveOrgId)?.name;
      const orgName = resolvedName || `Unknown org (${effectiveOrgId.slice(0, 8)}…)`;
      const orgType = jwtOrgNames.get(effectiveOrgId)?.type || 'CLIENT';

      if (!groups.has(effectiveOrgId)) {
        groups.set(effectiveOrgId, { orgName, orgType, unresolved: !resolvedName, projects: [] });
      }
      groups.get(effectiveOrgId)!.projects.push({ id: project.id, name: project.name });
    }

    // Sort: agency orgs first, then alphabetically
    return Array.from(groups.entries())
      .sort(([, a], [, b]) => {
        if (a.orgType === 'AGENCY' && b.orgType !== 'AGENCY') return -1;
        if (a.orgType !== 'AGENCY' && b.orgType === 'AGENCY') return 1;
        return a.orgName.localeCompare(b.orgName);
      });
  }, [projects, user?.organizations]);

  // Auto-expand orgs that have selected projects (for edit mode)
  useEffect(() => {
    if (selectedProjectIds.length > 0 && projectsByOrg.length > 0) {
      const selectedSet = new Set(selectedProjectIds);
      const toExpand = new Set<string>();
      for (const [orgId, { projects: orgProjects }] of projectsByOrg) {
        if (orgProjects.some(p => selectedSet.has(p.id))) {
          toExpand.add(orgId);
        }
      }
      setExpandedOrgs(toExpand);
    }
  }, [announcement, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter projectsByOrg based on search query
  const filteredProjectsByOrg = useMemo(() => {
    if (!orgSearch.trim()) return projectsByOrg;
    const q = orgSearch.toLowerCase().trim();
    return projectsByOrg
      .map(([orgId, group]) => {
        const nameMatch = group.orgName.toLowerCase().includes(q);
        const matchingProjects = nameMatch
          ? group.projects
          : group.projects.filter(p => p.name.toLowerCase().includes(q));
        if (matchingProjects.length === 0) return null;
        return [orgId, { ...group, projects: matchingProjects }] as [string, typeof group];
      })
      .filter(Boolean) as typeof projectsByOrg;
  }, [projectsByOrg, orgSearch]);

  const selectionSummary = useMemo(() => ({
    count: selectedProjectIds.length,
    total: projects?.length || 0,
  }), [selectedProjectIds, projects]);

  const toggleOrgExpand = useCallback((orgId: string) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }, []);

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof announcementsApi.create>[0]) =>
      announcementsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      titleDraft.clear();
      contentDraft.clear();
      setError(null);
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof announcementsApi.update>[1] }) =>
      announcementsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      titleDraft.clear();
      contentDraft.clear();
      setError(null);
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) return;
    
    const data = {
      title: title.trim(),
      content: content.trim(),
      allProjects,
      projectIds: allProjects ? undefined : selectedProjectIds,
      startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
      endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
    };
    
    if (announcement) {
      updateMutation.mutate({ id: announcement.id, data });
    } else {
      createMutation.mutate({ ...data, notifyUsers });
    }
  };
  
  const isPending = createMutation.isPending || updateMutation.isPending;
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-grey-100 shrink-0">
          <h2 className="text-heading-4 text-secondary">
            {announcement ? 'Edit Announcement' : 'New Announcement'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-1.5">
              Title <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); titleDraft.write(e.target.value); }}
              placeholder="Announcement title..."
              className="w-full px-4 py-2.5 border border-grey-200 rounded-lg text-secondary placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
              autoFocus
            />
          </div>
          
          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-1.5">
              Content
            </label>
            <RichTextEditor
              content={content}
              onChange={(html) => { setContent(html); contentDraft.write(html); }}
              placeholder="Write your announcement..."
              minHeight="150px"
            />
          </div>
          
          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-1.5">
              Target Projects
            </label>

            <div className="space-y-3">
              {/* All projects (global) */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allProjects}
                  onChange={(e) => {
                    setAllProjects(e.target.checked);
                    if (e.target.checked) setSelectedProjectIds([]);
                  }}
                  className="w-4 h-4 rounded border-grey-300 text-primary focus:ring-primary/30"
                />
                <span className="text-sm font-medium text-secondary">All projects</span>
              </label>

              {/* Grouped by organization - searchable & collapsible */}
              {!allProjects && (
                <div>
                  {/* Selection summary */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-grey-500">
                      {selectionSummary.count > 0
                        ? `${selectionSummary.count} of ${selectionSummary.total} projects selected`
                        : `${selectionSummary.total} projects available`}
                    </span>
                    <div className="flex items-center gap-3">
                      {selectionSummary.count > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedProjectIds([])}
                          className="text-xs text-grey-400 hover:text-grey-600"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setListExpanded((v) => !v)}
                        className="flex items-center gap-1 text-xs text-grey-400 hover:text-grey-600"
                      >
                        {listExpanded ? (
                          <>
                            <Minimize2 className="w-3 h-3" />
                            Collapse
                          </>
                        ) : (
                          <>
                            <Maximize2 className="w-3 h-3" />
                            Expand
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Search input - show when 5+ org groups */}
                  {projectsByOrg.length > 5 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-grey-400" />
                      <input
                        type="text"
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        placeholder="Search organizations or projects..."
                        className="w-full pl-8 pr-8 py-2 text-sm border border-grey-200 rounded-lg placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
                      />
                      {orgSearch && (
                        <button
                          type="button"
                          onClick={() => setOrgSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Org groups */}
                  <div
                    className={cn(
                      'overflow-y-auto rounded-lg border border-grey-200 divide-y divide-grey-100 transition-[max-height] duration-200',
                      listExpanded ? 'max-h-[60vh]' : 'max-h-72'
                    )}
                  >
                    {filteredProjectsByOrg.length === 0 ? (
                      <div className="py-4 text-center text-sm text-grey-400">
                        No matching organizations or projects
                      </div>
                    ) : (
                      filteredProjectsByOrg.map(([orgId, { orgName, unresolved, projects: orgProjects }]) => {
                        const orgProjectIds = orgProjects.map(p => p.id);
                        const allOrgSelected = orgProjectIds.every(id => selectedProjectIds.includes(id));
                        const someOrgSelected = orgProjectIds.some(id => selectedProjectIds.includes(id));
                        const selectedCount = orgProjectIds.filter(id => selectedProjectIds.includes(id)).length;
                        const isExpanded = orgSearch.trim() ? true : expandedOrgs.has(orgId);

                        return (
                          <div key={orgId}>
                            {/* Org header - clickable to expand/collapse */}
                            <div className="flex items-center gap-2 py-2 px-3 hover:bg-grey-50 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={allOrgSelected}
                                ref={(el) => { if (el) el.indeterminate = someOrgSelected && !allOrgSelected; }}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedProjectIds(prev => [...new Set([...prev, ...orgProjectIds])]);
                                  } else {
                                    setSelectedProjectIds(prev => prev.filter(id => !orgProjectIds.includes(id)));
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-grey-300 text-primary focus:ring-primary/30"
                              />
                              <button
                                type="button"
                                onClick={() => toggleOrgExpand(orgId)}
                                className="flex-1 flex items-center justify-between min-w-0"
                              >
                                <span
                                  className={cn(
                                    'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider truncate',
                                    unresolved ? 'text-amber-600' : 'text-grey-600'
                                  )}
                                  title={unresolved
                                    ? `This project's client organization (${orgId}) no longer exists or has lost Hub access. Set a valid client organization in Project Settings → Details.`
                                    : undefined}
                                >
                                  {unresolved && <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                                  {orgName}
                                </span>
                                <div className="flex items-center gap-2 shrink-0 ml-2">
                                  {selectedCount > 0 ? (
                                    <span className="text-xs text-primary font-medium bg-primary/10 px-1.5 py-0.5 rounded-full">
                                      {selectedCount}/{orgProjects.length}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-grey-400">
                                      {orgProjects.length}
                                    </span>
                                  )}
                                  {isExpanded ? (
                                    <ChevronUp className="w-3.5 h-3.5 text-grey-400" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-grey-400" />
                                  )}
                                </div>
                              </button>
                            </div>

                            {/* Individual projects - visible when expanded */}
                            {isExpanded && (
                              <div className="py-1.5 px-3 pl-9 space-y-0.5">
                                {orgProjects.map(project => (
                                  <label key={project.id} className="flex items-center gap-2 cursor-pointer py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={selectedProjectIds.includes(project.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedProjectIds(prev => [...prev, project.id]);
                                        } else {
                                          setSelectedProjectIds(prev => prev.filter(id => id !== project.id));
                                        }
                                      }}
                                      className="w-3.5 h-3.5 rounded border-grey-300 text-primary focus:ring-primary/30"
                                    />
                                    <span className="text-sm text-secondary truncate">{project.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Date/Time Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-grey-400" />
                  Starts At
                </div>
              </label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full px-4 py-2.5 border border-grey-200 rounded-lg text-secondary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
              />
              <p className="text-xs text-grey-400 mt-1">Leave empty to start immediately</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-grey-400" />
                  Ends At
                </div>
              </label>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full px-4 py-2.5 border border-grey-200 rounded-lg text-secondary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
              />
              <p className="text-xs text-grey-400 mt-1">Leave empty for no expiration</p>
            </div>
          </div>

          {/* Email notification - only when creating */}
          {!announcement && (
            <div className="bg-grey-50 border border-grey-200 rounded-lg p-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyUsers}
                  onChange={(e) => setNotifyUsers(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-grey-300 text-primary focus:ring-primary/30"
                />
                <span>
                  <span className="block text-sm font-medium text-secondary">
                    Send email notification
                  </span>
                  <span className="block text-xs text-grey-500 mt-0.5">
                    Emails every member of the targeted projects when the announcement starts
                    {allProjects && ' — with "All projects" selected, this goes to everyone in the system'}.
                    Sends immediately if no start date is set. Users who have turned off announcement
                    emails in their notification settings will not receive it.
                  </span>
                </span>
              </label>
            </div>
          )}

          </div>

          {/* Actions - pinned to the bottom of the modal, outside the scroll area */}
          <div className="shrink-0 flex justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || (!allProjects && selectedProjectIds.length === 0)}
              isLoading={isPending}
            >
              {announcement ? 'Save Changes' : 'Create Announcement'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Announcements() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  const { data: announcements, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => announcementsApi.list(),
  });

  const announcementId = searchParams.get('announcementId');
  const showModal = announcementId !== null;
  const editingAnnouncement = useMemo(() => {
    if (!announcementId || announcementId === 'new' || !announcements) return null;
    return announcements.find(a => a.id === announcementId) ?? null;
  }, [announcementId, announcements]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      setDeleteConfirmId(null);
    },
  });
  
  const handleNewAnnouncement = () => {
    setSearchParams({ announcementId: 'new' });
  };

  const handleEdit = (announcement: Announcement) => {
    setSearchParams({ announcementId: announcement.id });
  };

  const handleCloseModal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('announcementId');
    setSearchParams(next, { replace: true });
  };
  
  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-6 h-8 w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <PageHeader
        title="Announcements"
        subtitle="Manage announcements displayed on project pages"
        actions={
          <Button onClick={handleNewAnnouncement}>
            <Plus className="w-4 h-4" />
            New Announcement
          </Button>
        }
      />
      
      {/* Announcements List */}
      {!announcements || announcements.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No announcements yet"
          description="Create announcements to notify project members about important updates."
          action={
            <Button onClick={handleNewAnnouncement}>
              <Plus className="w-4 h-4" />
              Create First Announcement
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => {
            const status = statusConfig[announcement.status as keyof typeof statusConfig] || statusConfig.draft;
            const StatusIcon = status.icon;
            const isExpanded = expandedId === announcement.id;
            const isLongContent = announcement.content.length > 200;
            
            return (
              <Card key={announcement.id} className="overflow-hidden">
                <div className="flex items-start gap-4">
                  {/* Status indicator */}
                  <div className={cn('p-2 rounded-lg', status.bg)}>
                    <StatusIcon className={cn('w-5 h-5', status.color)} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Title & Status Badge */}
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-secondary">{announcement.title}</h3>
                      <span className={cn(
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        status.bg, status.color
                      )}>
                        {status.label}
                      </span>
                    </div>
                    
                    {/* Content preview */}
                    <div
                      className={cn(
                        'text-sm text-grey-600 prose prose-sm max-w-none',
                        !isExpanded && isLongContent && 'line-clamp-2'
                      )}
                      dangerouslySetInnerHTML={{ __html: announcement.content }}
                    />
                    
                    {isLongContent && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : announcement.id)}
                        className="mt-1 text-xs text-accent hover:underline flex items-center gap-0.5"
                      >
                        {isExpanded ? (
                          <>
                            Show less <ChevronUp className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            Show more <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Meta info */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-grey-400 flex-wrap">
                      <span>
                        Created {new Date(announcement.createdAt).toLocaleDateString()} by {announcement.createdBy?.name || 'Unknown'}
                      </span>
                      {announcement.startsAt && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Starts: {new Date(announcement.startsAt).toLocaleString()}
                        </span>
                      )}
                      {announcement.endsAt && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Ends: {new Date(announcement.endsAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    
                    {/* Projects */}
                    {announcement.projects && announcement.projects.length > 0 && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-grey-500">Projects:</span>
                        {announcement.projects.slice(0, 3).map((project) => (
                          <span
                            key={project.id}
                            className="text-xs bg-grey-100 text-grey-600 px-2 py-0.5 rounded-full"
                          >
                            {project.name}
                          </span>
                        ))}
                        {announcement.projects.length > 3 && (
                          <span className="text-xs text-grey-400">
                            +{announcement.projects.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 hover:bg-grey-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4 text-grey-400" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(announcement.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-grey-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Create/Edit Modal */}
      <AnnouncementModal
        isOpen={showModal}
        onClose={handleCloseModal}
        announcement={editingAnnouncement}
      />
      
      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Announcement?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This will permanently remove the announcement from all projects.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


